// `avatar comm-connect` (alias: `avatar chatops-connect`) — kết nối Chatops
// (Mattermost) cho workspace dev.
//
// Ported from avatar-po-cli phase-04 (`avatar-po comm-connect`) — adapted cho
// avatar CLI: `assertInAvatarWorkspaceOrExit` (marker .claude/avatar.json,
// KHÔNG .claude/avatar-po.json), command name giữ "comm-connect" (không đổi —
// contract phase-05 gọi đúng tên này ở cả 2 CLI).
//
// Dev cung cấp bot token (mint sẵn từ admin, DÙNG CHUNG với PO — risks[4] phase-05,
// chấp nhận nội bộ) + tên channel Kanban. CLI validate token qua Mattermost API
// (3-call chain: user → teams → channel) + xác nhận đọc được board Kanban, rồi lưu
// creds vào <workspace>/.mattermost/credentials.json (gitignored, chmod 600).
// team-ai-pack đọc store này lúc runtime (A2 — không hỏi lại, không hardcode).
//
// CLI↔pack contract (LOCKED — blueprint HTML §A2, khớp avatar-po-cli + po-ai-pack):
// 6 field `mattermostUrl, botName, botToken, teamId, defaultChannelId,
// defaultChannelName` tại path `.mattermost/credentials.json`.
import { join } from "node:path";
import boxen from "boxen";
import type { Command } from "commander";
import ora from "ora";
import { assertInAvatarWorkspaceOrExit } from "../lib/assert-in-avatar-workspace-or-hint-foreign.js";
import { assertMattermostUrlSafe } from "../lib/assert-mattermost-url-safe.js";
import {
  type CommSetupFlags,
  pickTeamByFlag,
  registerCommSetupFlags,
  resolveCommFlagsOrThrow,
} from "../lib/comm-setup-non-interactive-flags.js";
import { fetchBotTokenFromServer } from "../lib/fetch-mattermost-bot-token-from-server.js";
import { ensureDir, pathExists, readText, writeJsonAtomic } from "../lib/filesystem-helpers.js";
import { getBotToken, saveBotToken } from "../lib/mattermost-token-keychain-store.js";
import { isMissingFlagError, isNonInteractive } from "../lib/non-interactive-context.js";
import { inputOrFlag, passwordOrFlag, selectOrFlag } from "../lib/prompt-or-flag.js";
import type {
  AmbiguousChannelCandidate,
  ResolvedMattermostChannel,
} from "../lib/resolve-mattermost-bot-channel-via-api-chain.js";
import {
  MattermostSetupError,
  validateBotTokenAndResolveChannel,
} from "../lib/resolve-mattermost-bot-channel-via-api-chain.js";
import { chalk, log } from "../lib/terminal-logger.js";

// Default Mattermost server + bot name. Self-hosted khác: đổi qua --mattermost-url.
export const DEFAULT_MM_URL = "https://mm.nal.vn";
export const DEFAULT_BOT_NAME = "avatar";
export const SECRET_FILE_MODE = 0o600; // owner-only.

interface CommConnectOptions extends CommSetupFlags {
  mattermostUrl?: string;
}

// 6 field A2 LOCKED — pack đọc đúng schema này. KHÔNG đổi tên field.
export interface MattermostCredentials {
  mattermostUrl: string;
  botName: string;
  botToken: string;
  teamId: string;
  defaultChannelId: string;
  defaultChannelName: string;
}

// Re-export cho test/orchestrator import từ 1 chỗ. Chain thật sống ở lib/ (file-size guideline).
export { validateBotTokenAndResolveChannel };

// Mask token: chỉ hiện 4 ký tự cuối. KHÔNG bao giờ in full token. Exported để test.
export function maskToken(token: string): string {
  const suffix = token.slice(-4);
  return `${"•".repeat(Math.max(0, token.length - 4))}${suffix}`;
}

// Ghi <ws>/.mattermost/credentials.json 6 field A2 chmod 600 + gitignore.
// Tách + exported để test (CLI↔pack contract: field names + path).
export async function persistMattermostCredentials(
  workspaceRoot: string,
  creds: MattermostCredentials,
): Promise<string> {
  const mmDir = join(workspaceRoot, ".mattermost");
  await ensureDir(mmDir);
  await ensureMattermostGitignored(workspaceRoot);
  const credPath = join(mmDir, "credentials.json");
  await writeJsonAtomic(credPath, creds, SECRET_FILE_MODE);
  return credPath;
}

// Đảm bảo .mattermost/ được gitignore (append nếu chưa có). Idempotent.
async function ensureMattermostGitignored(workspaceRoot: string): Promise<void> {
  const gitignorePath = join(workspaceRoot, ".gitignore");
  const entry = ".mattermost/";
  let content = "";
  if (await pathExists(gitignorePath)) {
    content = await readText(gitignorePath);
    if (content.split("\n").some((l) => l.trim() === entry)) return; // đã có.
  }
  const { promises: fs } = await import("node:fs");
  const prefix = content.length > 0 && !content.endsWith("\n") ? "\n" : "";
  await fs.appendFile(gitignorePath, `${prefix}${entry}\n`);
}

const COMMAND = "comm-connect";
const BOT_NAME_FLAG_SPEC = {
  flag: "--comm-bot-name",
  command: COMMAND,
  hint: "--comm-bot-name avatar",
};
const CHANNEL_FLAG_SPEC = {
  flag: "--comm-channel",
  command: COMMAND,
  hint: "--comm-channel team/channel",
};
const TEAM_FLAG_SPEC = { flag: "--comm-team", command: COMMAND, hint: "--comm-team <team-slug>" };
const TOKEN_FLAG_SPEC = {
  flag: "--comm-bot-token-stdin",
  command: COMMAND,
  hint: 'echo "$TOKEN" | avatar comm-connect --non-interactive --comm-bot-token-stdin …',
};
const VALIDATE_FAIL_FLAG_SPEC = {
  flag: "--on-comm-validate-fail",
  command: COMMAND,
  hint: "--on-comm-validate-fail abort|defer",
};

async function runCommConnect(opts: CommConnectOptions): Promise<void> {
  const baseUrl = (opts.mattermostUrl ?? DEFAULT_MM_URL).replace(/\/+$/, "");
  // P2 A3: hardening URL trước khi gửi Bearer bot token (ép https host non-local
  // + allowlist host). Throw → command in message + thoát.
  assertMattermostUrlSafe(baseUrl);

  // Resolve workspace root (credentials per-workspace, không phải ~/.avatar).
  const workspaceRoot = assertInAvatarWorkspaceOrExit(process.cwd());

  // Chế độ non-interactive: kiểm ĐỦ cờ + đọc token TRƯỚC mọi lời gọi mạng, để ca
  // âm thoát nhanh và hermetic. Có TTY thì `flags` là undefined và wizard hỏi như cũ.
  const flags = isNonInteractive()
    ? await resolveCommFlagsOrThrow(opts, "comm-connect")
    : undefined;

  const botName = (
    await inputOrFlag(flags?.botName, BOT_NAME_FLAG_SPEC, {
      message: "Tên bot Mattermost:",
      default: DEFAULT_BOT_NAME,
    })
  ).trim();

  let channelName = (
    await inputOrFlag(flags?.channelName, CHANNEL_FLAG_SPEC, {
      message: "Tên channel chat (hoặc team/channel):",
      validate: (v) => (v.trim().length > 0 ? true : "Không được rỗng"),
    })
  ).trim();

  // Thứ tự ưu tiên cho bot mặc định "avatar": (1) Keychain — nhanh, offline; (2)
  // miss → server NAL get-bot-token — cần đã `avatar login`; (3) cả 2 fail → hỏi
  // tay. Bot khác "avatar" giữ nguyên: Keychain → hỏi tay (server chỉ có token avatar).
  // Non-interactive: token CHỈ từ stdin (xem quyết định ở comm-setup-non-interactive-flags.ts).
  // Không hỏi keychain/server ⇒ không dùng ngầm credential mà máy gọi không khai.
  const savedToken =
    flags === undefined && botName === DEFAULT_BOT_NAME ? await getBotToken(botName) : null;
  const serverToken =
    flags === undefined && !savedToken && botName === DEFAULT_BOT_NAME
      ? await fetchBotTokenFromServer(botName)
      : null;
  let token: string;
  if (flags) {
    token = flags.botToken;
  } else if (savedToken) {
    log.info("Dùng bot token @avatar đã lưu trong keychain.");
    token = savedToken;
  } else if (serverToken) {
    log.info("Đã lấy bot token @avatar từ server NAL.");
    token = serverToken;
    await saveBotToken(botName, token);
  } else {
    if (botName === DEFAULT_BOT_NAME) {
      log.dim("Chưa login? Chạy avatar login, hoặc dán token thủ công:");
    }
    token = (
      await passwordOrFlag(undefined, TOKEN_FLAG_SPEC, {
        message: "Dán bot token Mattermost:",
        mask: "*",
      })
    ).trim();
    if (!token) {
      log.error("Token rỗng — hủy.");
      process.exit(1);
      return;
    }
  }

  // Validate + resolve channel (3-call chain + board check). Retry-loop: fail →
  // menu sửa (điền lại channel / nhập lại token) hoặc defer (thoát êm, KHÔNG
  // process.exit(1) — defer là user chủ động, không phải lỗi).
  let resolved: ResolvedMattermostChannel;
  for (;;) {
    const spinner = ora("Kiểm tra kết nối Mattermost...").start();
    try {
      resolved = await validateBotTokenAndResolveChannel(
        baseUrl,
        token,
        botName,
        channelName,
        async (candidates: AmbiguousChannelCandidate[]) =>
          flags
            ? pickTeamByFlag(candidates, opts.commTeam, "comm-connect")
            : selectOrFlag(undefined, TEAM_FLAG_SPEC, {
                message: `Channel có ở ${candidates.length} team — chọn:`,
                choices: candidates.map((c) => ({
                  name: `${c.teamName}/${channelName} (${c.teamDisplayName})`,
                  value: c,
                })),
              }),
      );
      spinner.succeed(`Kết nối thành công — bot: ${resolved.username}, channel: ${channelName}`);
      break;
    } catch (err) {
      const kind = err instanceof MattermostSetupError ? err.kind : "other";
      const message = err instanceof Error ? err.message : String(err);
      spinner.fail(message);

      const isTokenError = kind === "token";

      // Non-interactive: không có ai chọn menu sửa. `--on-comm-validate-fail`
      // quyết định — mặc định `abort` (ném lại lỗi gốc, KHÔNG ghi credential nào).
      if (flags) {
        if (flags.onValidateFail === "defer") {
          log.dim("Validate thất bại — hoãn theo --on-comm-validate-fail=defer.");
          return;
        }
        throw err;
      }

      const fixChoice = await selectOrFlag(undefined, VALIDATE_FAIL_FLAG_SPEC, {
        message: "Xử lý thế nào?",
        choices: [
          {
            name: isTokenError ? "Nhập lại token" : "Điền lại tên channel",
            value: "retry",
          },
          { name: "Setup lại sau với lệnh avatar chatops-connect", value: "defer" },
        ],
      });

      if (fixChoice === "defer") {
        log.dim("Đã hủy — chạy avatar chatops-connect sau khi sẵn sàng.");
        return;
      }

      if (isTokenError) {
        token = (
          await passwordOrFlag(undefined, TOKEN_FLAG_SPEC, {
            message: "Dán bot token Mattermost:",
            mask: "*",
          })
        ).trim();
        if (!token) {
          log.error("Token rỗng — hủy.");
          process.exit(1);
          return;
        }
      } else {
        channelName = (
          await inputOrFlag(undefined, CHANNEL_FLAG_SPEC, {
            message: "Tên channel chat (hoặc team/channel):",
            validate: (v) => (v.trim().length > 0 ? true : "Không được rỗng"),
          })
        ).trim();
      }
      // loop lại để validate với field đã re-prompt.
    }
  }

  // Validate thành công → lưu lại token cho lần sau (kể cả bot khác avatar).
  await saveBotToken(botName, token);

  // Ghi credentials: <ws>/.mattermost/credentials.json 6 field A2 chmod 600.
  const creds: MattermostCredentials = {
    mattermostUrl: baseUrl,
    botName,
    botToken: token,
    teamId: resolved.teamId,
    defaultChannelId: resolved.channelId,
    defaultChannelName: resolved.channelName,
  };
  await persistMattermostCredentials(workspaceRoot, creds);

  // Success box — chỉ hiện masked token suffix (KHÔNG raw token).
  const lines = [
    `${chalk.bold("Mattermost connected")} ✓`,
    "─".repeat(40),
    `${chalk.dim("URL:")}        ${baseUrl}`,
    `${chalk.dim("Bot:")}        ${botName}`,
    `${chalk.dim("Channel:")}    ${resolved.channelName}`,
    `${chalk.dim("Team:")}       ${resolved.teamId}`,
    `${chalk.dim("Token:")}      ${maskToken(token)}`,
    `${chalk.dim("Saved:")}      .mattermost/credentials.json (chmod 600, gitignored)`,
  ];
  process.stdout.write(`${boxen(lines.join("\n"), { padding: 1, borderStyle: "round" })}\n`);
}

export function registerCommConnectCommand(program: Command): void {
  const cmd = program
    .command("comm-connect")
    .alias("chatops-connect")
    .description("Kết nối Chatops — Mattermost bot token + channel → .mattermost/credentials.json")
    .option("--mattermost-url <url>", `Base URL Mattermost (mặc định ${DEFAULT_MM_URL})`);
  registerCommSetupFlags(cmd, { withProvider: false });
  cmd.action(async (opts: CommConnectOptions) => {
    try {
      await runCommConnect(opts);
    } catch (err) {
      // Thiếu cờ phải đi tiếp lên `index.ts` để in JSON 1 dòng ra stderr; hạ nó
      // xuống `log.error` ở đây là làm mất đúng thứ máy gọi cần đọc.
      if (isMissingFlagError(err)) throw err;
      log.error(err instanceof Error ? err.message : String(err));
      process.exit(1);
    }
  });
}
